/**
 * @file Detects whether the active profile of an investment belongs to a non-U.S. person.
 */
import { INVESTMENT_PROFILE_FIELD_MAP } from "@components/Domain";

import type { ResidenceProfile } from "./types";

type ProfileField = keyof typeof INVESTMENT_PROFILE_FIELD_MAP;

type Investment = {
  profileType?: string;
  profileJoint?: ResidenceProfile;
  profileSdira?: ResidenceProfile;
  profileEntity?: ResidenceProfile;
  profileIndividual?: ResidenceProfile;
};

/**
 * Detects whether the profile that matches the investment's profile type answered No to the U.S.-person question.
 *
 * The other profiles of the investment are ignored, because they can hold the answers of a previous profile type.
 * A U.S. citizen is a U.S. person, so the hidden U.S.-person answer of a citizen is ignored as well.
 */
const isNonUsPersonInvestment = (investment: Investment): boolean => {
  const { profileType } = investment;

  if (!profileType) {
    return false;
  }

  const profileField = INVESTMENT_PROFILE_FIELD_MAP[profileType as ProfileField];

  if (!profileField) {
    return false;
  }

  const profile = investment[profileField];

  if (!profile) {
    return false;
  }

  const { isUsCitizen, isUsPerson } = profile;

  const isNotUsCitizen = !isUsCitizen;
  const isNotUsPerson = isUsPerson === false;

  return isNotUsCitizen && isNotUsPerson;
};

export default isNonUsPersonInvestment;
